import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import type { ProductIntegrationNeed } from '@/utils/productScope';
import {
  connectionMisconfig,
  syncRegistryMisconfig,
  type ParsedLoadError,
  type ProductConnectionTest
} from '@/utils/productScope';
import { useProductsMap } from '@/hooks/useProductsMap';

const CACHE_MS = 90_000;
const cache = new Map<string, { at: number; data: ProductConnectionTest }>();

type ConnectionState = {
  checking: boolean;
  syncError: ParsedLoadError | null;
  remoteError: ParsedLoadError | null;
  ready: boolean;
};

export function useProductConnection(
  productId: string | undefined,
  need: ProductIntegrationNeed
): ConnectionState {
  const productsById = useProductsMap();
  const [checking, setChecking] = useState(false);
  const [syncError, setSyncError] = useState<ParsedLoadError | null>(null);
  const [remoteError, setRemoteError] = useState<ParsedLoadError | null>(null);
  const [ready, setReady] = useState(need === 'core');
  const runId = useRef(0);

  const runCheck = useCallback(async () => {
    const id = productId?.trim();
    if (!id || need === 'core') {
      setSyncError(null);
      setRemoteError(null);
      setReady(true);
      setChecking(false);
      return;
    }

    const product = productsById.get(id);
    const sync = syncRegistryMisconfig(product, need);
    setSyncError(sync);
    if (sync) {
      setRemoteError(null);
      setReady(false);
      setChecking(false);
      return;
    }

    const cached = cache.get(`${id}:${need}`);
    if (cached && Date.now() - cached.at < CACHE_MS) {
      const mis = connectionMisconfig(cached.data, need);
      setRemoteError(mis);
      setReady(!mis);
      setChecking(false);
      return;
    }

    const myRun = ++runId.current;
    setChecking(true);
    setRemoteError(null);
    setReady(false);
    try {
      const res = await apiService.testProductConnection(id);
      const data = res.data as ProductConnectionTest;
      cache.set(`${id}:${need}`, { at: Date.now(), data });
      if (myRun !== runId.current) return;
      const mis = connectionMisconfig(data, need);
      setRemoteError(mis);
      setReady(!mis);
    } catch (err: unknown) {
      if (myRun !== runId.current) return;
      setRemoteError({
        kind: 'unreachable',
        title: 'Não foi possível validar a integração',
        detail:
          err instanceof Error
            ? err.message
            : 'Falha ao testar conexão com o produto. Tente novamente em instantes.'
      });
      setReady(false);
    } finally {
      if (myRun === runId.current) setChecking(false);
    }
  }, [productId, need, productsById]);

  useEffect(() => {
    runCheck().catch(() => undefined);
  }, [runCheck]);

  return {
    checking,
    syncError,
    remoteError,
    ready: ready && !syncError && !remoteError
  };
}
